import React from "react";

import {
  ButtonLink,
  CardList,
  CardListItem,
  Label,
  Typography,
  Tooltip,
  Button,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as FolderAddIcon } from "@king-ict/eupisi-ui/components/Icon/FolderAdd.svg";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import {
  getInstitutionDisplayNameAndTown,
  getStatusColor,
  hrDate,
} from "@king-ict/eupisi-ui/utils";
import { hrDateWithTime } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import { useTypedSelector } from "../../app/rootReducer";
import { useBusinessRule } from "../../hooks/businessRule/useBusinessRule";
import { EnrollmentCyclePedagogicalYear } from "../../models/EnrollmentCycle/EnrollmentCyclePedagogicalYear";
import EnrollmentTypeEnum from "../../models/EnrollmentType/EnrollmentTypeEnum";
import PedagogicalYear from "../../models/PedagogicalYear/PedagogicalYear";
import RequestListItem from "../../models/Request/RequestListItem";
import RequestStatusEnum from "../../models/Request/RequestStatusEnum";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";
import UpdateRequestsPriority from "../../models/Request/UpdateRequestPriority";
import CodeBook from "../../models/Shared/CodeBook";
import { newRequestUrl } from "../../routes/routes";
import { requestDetailsUrl, requestDraftDetailsUrl } from "../../routes/routes";
import { updateRequestPriorities } from "../../slices/requestSlice";
import styles from "./ChildRequestList.module.scss";

interface Props {
  pedagogicalYear: PedagogicalYear;
  childRequests: RequestListItem[];
  isMaximumRequestCountReached: boolean;
  childOib: string;
  childCountInfoExist: boolean;
  childDateOfBirth: Date;
  months: CodeBook[];
  enrollmentCyclesForPedagogicalYear: EnrollmentCyclePedagogicalYear[];
}

export default function ChildRequestList({
  pedagogicalYear,
  childRequests,
  isMaximumRequestCountReached,
  childOib,
  childCountInfoExist,
  months,
  enrollmentCyclesForPedagogicalYear,
}: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isRequestCreationAllowed } = useBusinessRule();

  const currentImportantEvent = useTypedSelector(
    (state) => state.pedagogicalYear.currentImportantDate
  );

  const nonCurrentRegularEnrollmentCycleIds: number[] = childRequests
    .filter(
      (r) =>
        r.importantDate.enrollmentTypeId ===
          EnrollmentTypeEnum.RegularEnrollment &&
        r.requestTypeId === RequestTypeEnum.Kindergarten &&
        r.importantDate.enrollmentCycleId !==
          currentImportantEvent.enrollmentCycleId
    )
    .map((r) => {
      return r.importantDate.enrollmentCycleId;
    });

  let nonCurrentRegularEnrollmentCycles: EnrollmentCyclePedagogicalYear[] = enrollmentCyclesForPedagogicalYear
    .filter((m) => nonCurrentRegularEnrollmentCycleIds.includes(m.id))
    .sort(
      (first, second) =>
        (second.dateFrom ? second.dateFrom.getTime() : 0) -
        (first.dateFrom ? first.dateFrom.getTime() : 0)
    );

  const handleOnDragEnd = async (source: number, destination: number) => {
    const updatedRequestPriorityList: UpdateRequestsPriority[] = [];
    const newChildRequests = [
      ...childRequests.filter(
        (x) =>
          x.importantDate.enrollmentTypeId ===
            EnrollmentTypeEnum.RegularEnrollment &&
          x.requestTypeId === RequestTypeEnum.Kindergarten &&
          x.importantDate.enrollmentCycleId ===
            currentImportantEvent.enrollmentCycleId
      ),
    ];

    let shiftedRequest = newChildRequests.splice(source, 1)[0];
    newChildRequests.splice(destination, 0, shiftedRequest);

    newChildRequests.forEach((request, index) => {
      updatedRequestPriorityList.push({
        requestId: request.id,
        priority: index + 1,
      });
    });

    dispatch(updateRequestPriorities(updatedRequestPriorityList));
  };

  const handleIsDraggable = (requests: RequestListItem[]): boolean => {
    return (
      pedagogicalYear.isActive &&
      requests.length > 1 &&
      requests.every((r) => r.status.id === RequestStatusEnum.Created) &&
      requests.every((r) => r.importantDate.id === currentImportantEvent.id)
    );
  };

  return (
    <>
      {childRequests.filter(
        (x) =>
          x.importantDate.enrollmentTypeId ===
            EnrollmentTypeEnum.RegularEnrollment &&
          x.requestTypeId === RequestTypeEnum.Kindergarten &&
          x.importantDate.enrollmentCycleId ===
            currentImportantEvent.enrollmentCycleId
      ).length === 0 ? (
        <div className={styles.emptyList}>
          <IconWrapper color="noted-2">
            <FolderAddIcon aria-label={t("accessibility.folderAddIcon")} />
          </IconWrapper>
          <Typography variant="largeBold">{`${t(
            "dashboard.noRequests"
          )}.`}</Typography>
          <Typography>{t("dashboard.noRequestsInfo")}</Typography>
        </div>
      ) : (
        <CardList
          isNumbered={true}
          isDraggable={handleIsDraggable(
            childRequests.filter(
              (x) =>
                x.importantDate.enrollmentTypeId ===
                  EnrollmentTypeEnum.RegularEnrollment &&
                x.requestTypeId === RequestTypeEnum.Kindergarten &&
                x.importantDate.enrollmentCycleId ===
                  currentImportantEvent.enrollmentCycleId
            )
          )}
          onDragEnd={handleOnDragEnd}
        >
          {childRequests
            .filter(
              (x) =>
                x.importantDate.enrollmentTypeId ===
                  EnrollmentTypeEnum.RegularEnrollment &&
                x.requestTypeId === RequestTypeEnum.Kindergarten &&
                x.importantDate.enrollmentCycleId ===
                  currentImportantEvent.enrollmentCycleId
            )
            .map((r) => (
              <CardListItem
                key={r.id === 0 ? r.guid : r.id}
                className={styles.cardListItem}
              >
                <Row className={styles.cardContent}>
                  <Col
                    xs={4}
                    className={`${styles.institution} ${styles.columnPading} ${styles.columnWidth}`}
                  >
                    <span>
                      {getInstitutionDisplayNameAndTown(
                        r.institution!.displayName,
                        r.institution!.town
                      )}
                    </span>
                  </Col>
                  <Col
                    xs={3}
                    className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                  >
                    <Label
                      className={styles.statusItem}
                      color={getStatusColor(r.status.id)}
                    >
                      {r.status.name}
                    </Label>
                  </Col>
                  <Col
                    xs={2}
                    className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                  >
                    <span>
                      {r.status.id !== 6 && r.dateUpdated
                        ? hrDateWithTime(r.dateUpdated)
                        : r.status.id !== 6 && hrDateWithTime(r.dateCreated)}
                    </span>
                  </Col>
                  <Col
                    xs={3}
                    className={`${styles.details} ${styles.columnPading} ${styles.columnWidth}`}
                  >
                    <ButtonLink
                      className={styles.details}
                      onClick={() =>
                        history.push(
                          r.status.id === RequestStatusEnum.Draft && r.guid
                            ? requestDraftDetailsUrl(r.guid)
                            : requestDetailsUrl(r.id)
                        )
                      }
                    >
                      {t("dashboard.detailsLink")}
                    </ButtonLink>
                  </Col>
                </Row>
              </CardListItem>
            ))}
        </CardList>
      )}
      {
        //<>
        //  {isPreschoolRequestAllowed(childDateOfBirth) && (
        //    <Row className={styles.root}>
        //      <Col xs={12} md={6}>
        //        <Typography variant="largeBold" component="h3">
        //          {t("common.preschoolEnrollment")}
        //        </Typography>
        //      </Col>
        //      <Col xs={12} md={12}>
        //        {childRequests.filter(
        //          (x) =>
        //            x.importantDate.enrollmentTypeId ===
        //              EnrollmentTypeEnum.RegularEnrollment &&
        //            x.requestTypeId === RequestTypeEnum.Preschool
        //        ).length > 0 ? null : (
        //          <div className={styles.emptyList}>
        //            <FolderAddIcon
        //              aria-label={t("accessibility.folderAddIcon")}
        //            />
        //            <p>{`${t("dashboard.noPreschoolRequests")}.`}</p>
        //            <p>{t("dashboard.noRequestsInfo")}</p>
        //          </div>
        //        )}
        //      </Col>
        //    </Row>
        //  )}
        //  <CardList isNumbered={true} isDraggable={false}>
        //    {childRequests
        //      .filter(
        //        (x) =>
        //          x.importantDate.enrollmentTypeId ===
        //            EnrollmentTypeEnum.RegularEnrollment &&
        //          x.requestTypeId === RequestTypeEnum.Preschool
        //      )
        //      .map((r) => (
        //        <CardListItem
        //          key={r.id === 0 ? r.guid : r.id}
        //          className={styles.cardListItem}
        //        >
        //          <Row className={styles.cardContent}>
        //            <Col
        //              xs={4}
        //              className={`${styles.institution} ${styles.columnPading} ${styles.columnWidth}`}
        //            >
        //              <span>
        //                {getInstitutionDisplayNameAndTown(
        //                  r.institution!.displayName,
        //                  r.institution!.town
        //                )}
        //              </span>
        //            </Col>
        //            <Col
        //              xs={4}
        //              className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
        //            >
        //              <Label
        //                className={styles.statusItem}
        //                color={getStatusColor(r.status.id)}
        //              >
        //                {r.status.name}
        //              </Label>
        //            </Col>
        //            <Col
        //              xs={4}
        //              className={`${styles.details} ${styles.columnPading} ${styles.columnWidth}`}
        //            >
        //              <ButtonLink
        //                className={styles.details}
        //                onClick={() =>
        //                  history.push(
        //                    r.status.id === RequestStatusEnum.Draft && r.guid
        //                      ? preschoolRequestDraftDetailsUrl(r.guid)
        //                      : preschoolRequestDetailsUrl(r.id)
        //                  )
        //                }
        //              >
        //                {t("dashboard.detailsLink")}
        //              </ButtonLink>
        //            </Col>
        //          </Row>
        //        </CardListItem>
        //      ))}
        //  </CardList>
        //</>
      }

      {nonCurrentRegularEnrollmentCycles.map((ec) => (
        <React.Fragment key={ec.id}>
          <Row className={styles.root}>
            <Col xs={12} md={6}>
              <Typography variant="largeBold" component="h3">
                {t("common.regularEnrollment") +
                  " " +
                  (ec.dateFrom ? hrDate(ec.dateFrom) + " - " : "") +
                  (ec.dateTo ? hrDate(ec.dateTo) : "")}
              </Typography>
            </Col>
          </Row>
          <CardList isNumbered={true} isDraggable={false}>
            {childRequests
              .filter(
                (x) =>
                  x.importantDate.enrollmentTypeId ===
                    EnrollmentTypeEnum.RegularEnrollment &&
                  x.importantDate.enrollmentCycleId === ec.id
              )
              .map((r) => (
                <CardListItem
                  key={r.id === 0 ? r.guid : r.id}
                  className={styles.cardListItem}
                >
                  <Row className={styles.cardContent}>
                    <Col
                      xs={4}
                      className={`${styles.institution} ${styles.columnPading} ${styles.columnWidth}`}
                    >
                      <span>
                        {getInstitutionDisplayNameAndTown(
                          r.institution!.displayName,
                          r.institution!.town
                        )}
                      </span>
                    </Col>
                    <Col
                      xs={3}
                      className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                    >
                      <Label
                        className={styles.statusItem}
                        color={getStatusColor(r.status.id)}
                      >
                        {r.status.name}
                      </Label>
                    </Col>
                    <Col
                      xs={2}
                      className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                    >
                      <span>
                        {r.status.id !== 6 && r.dateUpdated
                          ? hrDateWithTime(r.dateUpdated)
                          : r.status.id !== 6 && hrDateWithTime(r.dateCreated)}
                      </span>
                    </Col>
                    <Col
                      xs={3}
                      className={`${styles.details} ${styles.columnPading} ${styles.columnWidth}`}
                    >
                      <ButtonLink
                        className={styles.details}
                        onClick={() =>
                          history.push(
                            r.status.id === RequestStatusEnum.Draft && r.guid
                              ? requestDraftDetailsUrl(r.guid)
                              : requestDetailsUrl(r.id)
                          )
                        }
                      >
                        {t("dashboard.detailsLink")}
                      </ButtonLink>
                    </Col>
                  </Row>
                </CardListItem>
              ))}
          </CardList>
        </React.Fragment>
      ))}
      {months.map((month) => (
        <React.Fragment key={month.id}>
          {currentImportantEvent.monthId === month.id &&
          currentImportantEvent.enrollmentTypeId ===
            EnrollmentTypeEnum.SubsequentEnrollment ? (
            <Row className={styles.root}>
              <Col xs={12} md={6}>
                <Typography variant="largeBold" component="h3">
                  {t("common.additionalEnrollment")} {month.name}
                </Typography>
              </Col>
              {isRequestCreationAllowed() && (
                <Col xs={12} md={6} className={`${styles.actionContainer}`}>
                  <Tooltip
                    placement="bottom"
                    overlay={
                      isRequestCreationAllowed()
                        ? isMaximumRequestCountReached || childCountInfoExist
                          ? t("dashboard.createRequestInfoDisabled")
                          : t("dashboard.createRequestInfoEnabled", {
                              endDate: hrDateWithTime(
                                currentImportantEvent.dateTimeTo!
                              ),
                            })
                        : t("dashboard.requestCreationNotAllowed")
                    }
                  >
                    <IconWrapper color="primary">
                      <InfoIcon aria-label={t("accessibility.infoIcon")} />
                    </IconWrapper>
                  </Tooltip>
                  <Button
                    className={styles.actionButton}
                    color="primary"
                    disabled={
                      isMaximumRequestCountReached ||
                      !isRequestCreationAllowed() ||
                      childCountInfoExist
                    }
                    onClick={() => history.push(newRequestUrl(childOib))}
                  >
                    {t("dashboard.createRequestButton")}
                  </Button>
                </Col>
              )}
              <Col xs={12} md={12}>
                {childRequests.filter(
                  (x) =>
                    x.importantDate.enrollmentTypeId ===
                      EnrollmentTypeEnum.SubsequentEnrollment &&
                    x.importantDate.monthId === month.id
                ).length > 0 ? null : (
                  <div className={styles.emptyList}>
                    <IconWrapper color="noted-2">
                      <FolderAddIcon
                        aria-label={t("accessibility.folderAddIcon")}
                      />
                    </IconWrapper>
                    <Typography>{`${t(
                      "dashboard.noSubsequentRequests"
                    )}.`}</Typography>
                    <Typography>{t("dashboard.noRequestsInfo")}</Typography>
                  </div>
                )}
              </Col>
            </Row>
          ) : childRequests.filter(
              (x) =>
                x.importantDate.enrollmentTypeId ===
                  EnrollmentTypeEnum.SubsequentEnrollment &&
                x.importantDate.monthId === month.id
            ).length > 0 ? (
            <Row className={styles.root}>
              <Col xs={12} md={6}>
                <Typography variant="largeBold" component="h3">
                  {t("common.additionalEnrollment")} {month.name}
                </Typography>
              </Col>
            </Row>
          ) : null}
          <CardList isNumbered={true} isDraggable={false}>
            {childRequests
              .filter(
                (x) =>
                  x.importantDate.enrollmentTypeId ===
                    EnrollmentTypeEnum.SubsequentEnrollment &&
                  x.importantDate.monthId === month.id
              )
              .map((r) => (
                <CardListItem
                  key={r.id === 0 ? r.guid : r.id}
                  className={styles.cardListItem}
                >
                  <Row className={styles.cardContent}>
                    <Col
                      xs={4}
                      className={`${styles.institution} ${styles.columnPading} ${styles.columnWidth}`}
                    >
                      <span>
                        {getInstitutionDisplayNameAndTown(
                          r.institution!.displayName,
                          r.institution!.town
                        )}
                      </span>
                    </Col>
                    <Col
                      xs={3}
                      className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                    >
                      <Label
                        className={styles.statusItem}
                        color={getStatusColor(r.status.id)}
                      >
                        {r.status.name}
                      </Label>
                    </Col>
                    <Col
                      xs={2}
                      className={`${styles.columnPading} ${styles.label} ${styles.columnWidth}`}
                    >
                      <span>
                        {r.status.id !== 6 && r.dateUpdated
                          ? hrDateWithTime(r.dateUpdated)
                          : r.status.id !== 6 && hrDateWithTime(r.dateCreated)}
                      </span>
                    </Col>
                    <Col
                      xs={3}
                      className={`${styles.details} ${styles.columnPading} ${styles.columnWidth}`}
                    >
                      <ButtonLink
                        className={styles.details}
                        onClick={() =>
                          history.push(
                            r.status.id === RequestStatusEnum.Draft && r.guid
                              ? requestDraftDetailsUrl(r.guid)
                              : requestDetailsUrl(r.id)
                          )
                        }
                      >
                        {t("dashboard.detailsLink")}
                      </ButtonLink>
                    </Col>
                  </Row>
                </CardListItem>
              ))}
          </CardList>
        </React.Fragment>
      ))}
    </>
  );
}
